import React, { lazy } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Layout } from 'antd'
import TrialBanner from './components/TrialBanner'

import GuestRoutes from './components/GuestRoutes'
import Loader from './components/Loader'

import {
	EMPLOYEES_ROOT,
	EMPLOYEES_SCHEDULING_ROUTE,
	EDIT_EMPLOYEE,
} from './modules/employees/utils/urls'

import { CALENDAR_ROOT } from './modules/calendar/utils/urls'
import { SERVICES_ROOT } from './modules/services/utils/urls'

import {
	LOCATIONS_ROOT,
	LOCATIONS_CLOSED_DATES,
	EDIT_LOCATION,
} from './modules/locations/utils/urls'

import {
	CUSTOMERS_ROOT,
	EDIT_CUSTOMER_ROUTE,
	CREATE_CUSTOMER_ROUTE,
	CUSTOMER_OVERVIEW,
} from './modules/customers/utils/urls'

import { setLogScope } from './common/utils/logging'

import { userInfoQuery } from './modules/authentication/queries'
import { setCalendarResource } from './modules/calendar/mutations'

import { ANALYTICS_ROOT } from 'modules/analytics/utils/urls'
import useMobile from 'common/hooks/useMobile'
import Overview from 'modules/overview/views/RootContainer'
import { OVERVIEW_ROOT } from 'modules/overview/utils/urls'
import { impt } from './apollo-client'
import usePageTracking from 'common/hooks/usePageTracking'

const MessageBlast = lazy(() => import('modules/marketing/views/MessageBlast'))
const InviteClient = lazy(() => import('modules/marketing/views/InviteClient'))

const MainMenu = lazy(() => import('./components/MainMenu'))
const MobileMainMenu = lazy(() => import('./components/MainMenu/MobileMainMenu'))

const Employees = lazy(() => import('./modules/employees/views/EmployeesRoot'))
const EmployeeScheduling = lazy(() => import('./modules/employees/views/EmployeeScheduling'))
const ManageEmployee = lazy(() => import('./modules/employees/views/ManageEmployee'))

const Locations = lazy(() => import('./modules/locations/views/RootContainer'))
const ManageLocation = lazy(() => import('./modules/locations/views/Manage/RootContainer'))

const ClosedDates = lazy(() => import('./modules/locations/views/ClosedDatesContainer'))

const Services = lazy(() => import('./modules/services/views/ServicesRoot'))
const Settings = lazy(() => import('./modules/settings/views/RootContainer'))

const Calendar = lazy(() => import('./modules/calendar/views/CalendarRoot'))

const CustomerModal = lazy(() => import('./modules/customers/views/CustomerModal'))
const CustomerList = lazy(() => import('./modules/customers/views/ListContainer'))
const CustomerOverview = lazy(() => import('./modules/customers/views/CustomerOverview'))

const Analytics = lazy(() => import('./modules/analytics/views/RootContainer'))

const App = () => {
	usePageTracking()

	const { width, isMobile: isMobileDevice } = useMobile()

	const collapsed = width < 900 || isMobileDevice

	const [setActiveResource] = useMutation(setCalendarResource)

	const { loading, data } = useQuery(userInfoQuery, {
		variables: {
			skip: !impt && !localStorage.getItem('nw-sess'),
		},
	})

	const user = data?.me

	React.useEffect(() => {
		setLogScope({ user })

		if (user && user.permissionLevel !== 'full' && user.permissionLevel !== 'owner') {
			try {
				setActiveResource({
					variables: {
						resource: user,
					},
				})
			} catch (e) {
				console.log(e)
			}
		}
	}, [user, setActiveResource])

	if (loading) return <Loader />

	if (!user) {
		return (
			<React.Suspense fallback={<Loader />}>
				<GuestRoutes />
			</React.Suspense>
		)
	}

	console.log(user.company.subscription.plan.accessLevel, user.company.locations.length > 1)

	return (
		<Layout>
			<React.Suspense fallback={<Loader />}>
				{isMobileDevice ? (
					<MobileMainMenu windowWidth={width} user={user} />
				) : (
					<Layout.Sider collapsed={collapsed} width="250" theme="dark">
						<MainMenu collapsed={collapsed} user={user} />
					</Layout.Sider>
				)}
			</React.Suspense>

			<TrialBanner
				collapsed={collapsed}
				windowWidth={width}
				isMobileDevice={isMobileDevice}
				subscription={user.company.subscription}
			/>

			<React.Suspense fallback={<Loader />}>
				<React.Suspense fallback={null}>
					<Route path={[CREATE_CUSTOMER_ROUTE, EDIT_CUSTOMER_ROUTE]}>
						<CustomerModal isMobileDevice={isMobileDevice} />
					</Route>
				</React.Suspense>

				<Switch>
					<Route path={CALENDAR_ROOT}>
						<Calendar
							isMobileDevice={isMobileDevice}
							locations={user.company.locations}
							user={user}
						/>
					</Route>

					<Route path={SERVICES_ROOT}>
						<Services isMobileDevice={isMobileDevice} />
					</Route>

					<Route path={EDIT_EMPLOYEE}>
						<ManageEmployee isMobileDevice={isMobileDevice} />
					</Route>

					<Route path={EMPLOYEES_ROOT}>
						<Employees
							isMobileDevice={isMobileDevice}
							totalAccountsAllowed={user.company.maxStaff}
						/>
					</Route>

					<Route path={EMPLOYEES_SCHEDULING_ROUTE}>
						<EmployeeScheduling
							isMobileDevice={isMobileDevice}
							locations={user.company.locations}
						/>
					</Route>

					<Route path={LOCATIONS_CLOSED_DATES}>
						<ClosedDates isMobileDevice={isMobileDevice} locations={user.company.locations} />
					</Route>

					<Route path={LOCATIONS_ROOT}>
						<Locations
							needsToUpgradeToCreateLocations={
								!user.company.subscription.isPayingCustomer ||
								user.company.subscription.plan.accessLevel === 'low'
							}
							hasExceededDeviceLimit={
								user.company.subscription.plan.accessLevel === 'low' &&
								user.company.locations.length > 1
							}
							isMobileDevice={isMobileDevice}
							locations={user.company.locations}
						/>
					</Route>

					<Route path={EDIT_LOCATION}>
						<ManageLocation isMobileDevice={isMobileDevice} />
					</Route>

					<Route path={CUSTOMERS_ROOT}>
						<Route path={CUSTOMERS_ROOT} exact>
							<CustomerList isMobileDevice={isMobileDevice} />
						</Route>

						<Route path={CUSTOMER_OVERVIEW}>
							<CustomerOverview />
						</Route>
					</Route>

					{user.permissionLevel === 'owner' && (
						<Route path={ANALYTICS_ROOT}>
							<Analytics isMobileDevice={isMobileDevice} />
						</Route>
					)}

					<Route path="/settings">
						<Settings windowWidth={width} user={user} />
					</Route>

					<Route path="/marketing">
						{user.company.features.includes('MESSAGE_BLAST') && (
							<Route path="/marketing/blast">
								<MessageBlast
									locations={user.company.locations}
									subscription={user.company.subscription}
								/>
							</Route>
						)}

						<Route path="/marketing/invite">
							<InviteClient locations={user.company.locations} />
						</Route>
					</Route>

					<Route path={OVERVIEW_ROOT}>
						<Overview isMobileDevice={isMobileDevice} user={user} userId={user.id} />
					</Route>

					<Redirect to={OVERVIEW_ROOT} />
				</Switch>
			</React.Suspense>
		</Layout>
	)
}

export default App
